import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import './Timeline.css';
import './TimelineRework.css';
import UpcomingByCourse from './UpcomingByCourse';

// TimelineRoot: Smart container for ScoutIQ Study Timeline (week view)
// This is adapted from the previous UpcomingTimeline component, relocated under src/scoutiq.
export default function TimelineRoot({ onAssignmentClick, onEventClick, onTimelineReady }) {
  const { getFilteredAssignments, getFilteredEvents, getFilteredEndeavors, getCurrentUser, dataRefreshTrigger } = useData?.() || {};
  const [, setAssignments] = useState([]);
  const [events, setEvents] = useState([]);
  const [endeavors, setEndeavors] = useState([]);
  const [mainLoading, setMainLoading] = useState(true);
  const [endeavorsLoading, setEndeavorsLoading] = useState(true);
  const [standalonesLoading, setStandalonesLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [courseFilter, setCourseFilter] = useState('all');
  const [timeWindow, setTimeWindow] = useState(14); // days: 7, 14, 30, or 60

  // Expose update functions to parent via callback
  useEffect(() => {
    if (onTimelineReady) {
      const updaters = {
        updateAssignment: (updatedAssignment) => {
          const targetId = updatedAssignment.assignmentId || updatedAssignment.id;
          setAssignments(prev => prev.map(a => ((a.assignmentId || a.id) === targetId ? updatedAssignment : a)));
        },
        updateEvent: (updatedEvent) => {
          const targetId = updatedEvent.eventId || updatedEvent.id;
          setEvents(prev => prev.map(e => ((e.eventId || e.id) === targetId ? updatedEvent : e)));
        }
      };
      onTimelineReady(updaters);
    }
  }, [onTimelineReady]);

  // Fetch user ID
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getCurrentUser) return;
      try {
        const user = await getCurrentUser();
        if (cancelled || !user) return;
        const id = user.userId || user.user_id || user.id;
        setUserId(id);
      } catch (e) {
        console.error('TimelineRoot: Failed to get user', e);
      }
    })();
    return () => { cancelled = true; };
  }, [getCurrentUser]);

  // Fetch endeavors
  useEffect(() => {
    if (!userId || !getFilteredEndeavors) return;
    let cancelled = false;
    setEndeavorsLoading(true);
    (async () => {
      try {
        const list = await getFilteredEndeavors(userId);
        if (cancelled) return;
        setEndeavors(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('TimelineRoot: Failed to fetch endeavors', e);
        if (!cancelled) setEndeavors([]);
      } finally {
        if (!cancelled) setEndeavorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, getFilteredEndeavors]);

  // Fetch assignments and events
  useEffect(() => {
    if (!getFilteredAssignments || !getFilteredEvents) {
      setMainLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setMainLoading(true);
      try {
        // Use hydrated cache (no size parameter needed - reads from cached data)
        const [asgList, evList] = await Promise.all([
          getFilteredAssignments(),
          getFilteredEvents()
        ]);
        if (cancelled) return;
        setAssignments(Array.isArray(asgList) ? asgList : []);
        setEvents(Array.isArray(evList) ? evList : []);
      } catch (e) {
        console.error('TimelineRoot: Failed to fetch data', e);
        if (!cancelled) {
          setAssignments([]);
          setEvents([]);
        }
      } finally {
        if (!cancelled) setMainLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [getFilteredAssignments, getFilteredEvents, dataRefreshTrigger]);

  const endeavorMap = useMemo(() => {
    const map = new Map();
    endeavors.forEach(e => {
      const id = e.id || e.endeavorId || e.endeavorID || e.endeavor_id;
      if (id) map.set(String(id), e);
    });
    return map;
  }, [endeavors]);



  // eslint-disable-next-line no-unused-vars
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return '';
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getCourseInfo = (endeavorId) => {
    if (!endeavorId) return { code: '', name: '', color: '#94a3b8' };
    const endeavor = endeavorMap.get(String(endeavorId));
    if (!endeavor) return { code: '', name: '', color: '#94a3b8' };
    return {
      code: endeavor.code || endeavor.shortName || endeavor.courseCode || '',
      name: endeavor.name || endeavor.courseName || endeavor.title || '',
      color: endeavor.color || endeavor.courseColor || '#94a3b8'
    };
  };

  // eslint-disable-next-line no-unused-vars
  const getCourseCode = useCallback((endeavorId) => {
    if (!endeavorId) return '';
    const endeavor = endeavorMap.get(String(endeavorId));
    if (!endeavor) return '';
    return endeavor.code || endeavor.shortName || endeavor.courseCode || '';
  }, [endeavorMap]);

  // Fetch standalone assignments (no eventId) for all courses
  const [standaloneAssignments, setStandaloneAssignments] = useState([]);

  useEffect(() => {
    if (!getFilteredAssignments || endeavorsLoading) return;
    if (endeavors.length === 0) {
      setStandalonesLoading(false);
      return;
    }
    let cancelled = false;
    setStandalonesLoading(true);
    (async () => {
      try {
        const promises = endeavors.map(async (endeavor) => {
          const endeavorId = endeavor.id || endeavor.endeavorId || endeavor.endeavorID || endeavor.endeavor_id;
          if (!endeavorId) return [];
          const asns = await getFilteredAssignments({ endeavorId, noEvent: true, completed: false });
          return Array.isArray(asns) ? asns : [];
        });
        const results = await Promise.all(promises);
        if (cancelled) return;
        const flattened = results.flat();
        setStandaloneAssignments(flattened);
      } catch (e) {
        console.error('TimelineRoot: Failed to fetch standalone assignments', e);
        if (!cancelled) setStandaloneAssignments([]);
      } finally {
        if (!cancelled) setStandalonesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getFilteredAssignments, endeavors, endeavorsLoading, dataRefreshTrigger]);

  // Create chronological array of events and standalone assignments
  const chronologicalItems = useMemo(() => {
    const now = Date.now();
    const endTime = now + (timeWindow * 24 * 60 * 60 * 1000);
    const items = [];

    const passesFilters = (item) => {
      if (courseFilter !== 'all') {
        const itemEndeavorId = item.endeavorId || item.endeavorID || item.endeavor_id;
        if (String(itemEndeavorId) !== String(courseFilter)) return false;
      }
      return true;
    };

    const toMs = (dateStr) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d.getTime();
    };

    // Add events within time window (only course-affiliated events belong on the study timeline;
    // personal events are shown on the calendar instead — see EventForm field-hint)
    events.forEach(evt => {
      const evtEndeavorId = evt.endeavorId || evt.endeavorID || evt.endeavor_id;
      if (!evtEndeavorId) return;
      const startRaw = evt.startDatetime || evt.eventDate || evt.startDate || evt.start_date || evt.start || evt.dueDate;
      const startMs = toMs(startRaw);
      if (!startMs || startMs < now || startMs > endTime) return;
      if (!passesFilters(evt)) return;
      
      items.push({
        type: 'event',
        data: evt,
        timestamp: startMs,
        id: evt.id || evt.eventId || evt.eventID
      });
    });

    // Add standalone assignments within time window
    standaloneAssignments.forEach(asg => {
      const dueRaw = asg.dueDatetime || asg.dueDate || asg.dueAt || asg.due_at || asg.due;
      const dueMs = toMs(dueRaw);
      if (!dueMs || dueMs < now || dueMs > endTime) return;
      if (!passesFilters(asg)) return;
      
      items.push({
        type: 'standaloneAssignment',
        data: asg,
        timestamp: dueMs,
        id: asg.id || asg.assignmentId || asg.assignmentID
      });
    });

    // Sort chronologically
    items.sort((a, b) => a.timestamp - b.timestamp);

    return items;
  }, [events, standaloneAssignments, courseFilter, timeWindow]);





  // All async stages must complete before we show content
  const loading = mainLoading || endeavorsLoading || standalonesLoading;

  if (loading) {
    return (
      <div className="upcoming-timeline" aria-label="Upcoming timeline">
        <div className="timeline-header">
          <h3 className="timeline-title">Study Timeline</h3>
        </div>
        <div className="timeline-loading">
          <div className="loading-spinner" />
          <p>Loading your schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="upcoming-timeline" aria-label="Upcoming timeline">
      <div data-tour="study-timeline" className="studyTimelineCard">
        {/* Unified header */}
        <div className="stl-header">
          <div className="stl-header-top">
            <div className="stl-header-title-group">
              <span className="stl-header-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </span>
              <h3 className="stl-header-title">Study Timeline</h3>
            </div>
            <div className="stl-header-controls">
              <select
                className="stl-pill-select"
                value={timeWindow}
                onChange={(e) => setTimeWindow(Number(e.target.value))}
                aria-label="Select time window"
              >
                <option value="7">1 Week</option>
                <option value="14">2 Weeks</option>
                <option value="30">1 Month</option>
                <option value="60">2 Months</option>
              </select>
              <select
                className="stl-pill-select"
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                aria-label="Filter by course"
              >
                <option value="all">All Courses</option>
                {endeavors.map(e => {
                  const id = e.id || e.endeavorId || e.endeavorID || e.endeavor_id;
                  const name = e.name || e.courseName || e.title || e.code || 'Unknown Course';
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <p className="stl-header-hint">Click circles to mark complete</p>
        </div>

        <UpcomingByCourse
          courses={endeavors}
          chronologicalItems={chronologicalItems}
          courseFilter={courseFilter}
        />

      </div>
    </div>
  );
}
